import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, X, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { statusWhatsAppLink } from "@/lib/whatsapp";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const statusStyle = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    confirmed: "bg-sky-100 text-sky-800 border-sky-200",
    shipped: "bg-indigo-100 text-indigo-800 border-indigo-200",
    delivered: "bg-forest-100 text-forest-800 border-forest-200",
    cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/orders")
            .then((r) => setOrders(r.data))
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return orders;
        return orders.filter((o) =>
            [o.order_number, o.customer_name, o.phone, o.city, o.product_name]
                .join(" ")
                .toLowerCase()
                .includes(q),
        );
    }, [query, orders]);

    const updateStatus = async (order_number, status) => {
        try {
            await api.patch(`/admin/orders/${order_number}/status`, { status });
            const order = orders.find((o) => o.order_number === order_number);
            toast.success(`Status updated to ${status}`, {
                duration: 10000,
                action: order?.phone
                    ? { label: "Send WhatsApp update", onClick: () => window.open(statusWhatsAppLink({ ...order, status }), "_blank") }
                    : undefined,
            });
            setOrders((os) =>
                os.map((o) =>
                    o.order_number === order_number ? { ...o, status } : o,
                ),
            );
            if (selected?.order_number === order_number) {
                setSelected({ ...selected, status });
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update");
        }
    };

    return (
        <div data-testid="admin-orders" className="space-y-6">
            <header>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-800">
                    Orders
                </h1>
                <p className="text-sm text-forest-900/70 mt-1">
                    Every COD order placed on your storefront.
                </p>
            </header>

            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-forest-700/50" />
                    <input
                        data-testid="orders-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by order #, name, phone, city…"
                        className="w-full bg-white border border-forest-100 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                    />
                </div>
                <span className="text-xs text-forest-700/70">
                    {filtered.length}{" "}
                    {filtered.length === 1 ? "result" : "results"}
                </span>
            </div>

            <div className="bg-white rounded-2xl border border-forest-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-forest-50 text-forest-800 text-left text-[11px] uppercase tracking-widest">
                            <tr>
                                <th className="px-4 py-3">Order #</th>
                                <th className="px-4 py-3">Customer</th>
                                <th className="px-4 py-3">Product</th>
                                <th className="px-4 py-3">Bundle</th>
                                <th className="px-4 py-3">Total</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-10 text-center text-forest-700/60">
                                        Loading orders…
                                    </td>
                                </tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-10 text-center text-forest-700/60">
                                        No orders found.
                                    </td>
                                </tr>
                            )}
                            {filtered.map((o) => (
                                <tr
                                    key={o.order_number}
                                    data-testid={`order-row-${o.order_number}`}
                                    className="border-t border-forest-50 hover:bg-forest-50/40"
                                >
                                    <td className="px-4 py-3 font-bold text-forest-800">
                                        #{o.order_number}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-forest-900">
                                            {o.customer_name}
                                        </div>
                                        <div className="text-xs text-forest-700/60">
                                            {o.phone} · {o.city}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-forest-800">
                                        {o.product_name}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-forest-700/80">
                                        {o.bundle_label}
                                    </td>
                                    <td className="px-4 py-3 font-serif font-bold text-forest-800">
                                        {o.total_price.toLocaleString()} PKR
                                    </td>
                                    <td className="px-4 py-3">
                                        <select
                                            value={o.status}
                                            onChange={(e) =>
                                                updateStatus(
                                                    o.order_number,
                                                    e.target.value,
                                                )
                                            }
                                            data-testid={`order-status-${o.order_number}`}
                                            className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${statusStyle[o.status] || "bg-forest-50"}`}
                                        >
                                            {STATUSES.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-forest-700/70">
                                        {new Date(o.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <a
                                                href={statusWhatsAppLink(o)}
                                                target="_blank"
                                                rel="noreferrer"
                                                data-testid={`status-wa-${o.order_number}`}
                                                className="inline-flex items-center gap-1 text-[11px] font-bold text-forest-700 hover:text-gold-600"
                                                title={`Send '${o.status}' WhatsApp update`}
                                            >
                                                <MessageCircle className="w-3.5 h-3.5" />
                                                WhatsApp
                                            </a>
                                            <button
                                                onClick={() => setSelected(o)}
                                                className="text-xs font-bold text-forest-700 hover:text-gold-600"
                                            >
                                                View
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && (
                <div
                    data-testid="order-details-modal"
                    className="fixed inset-0 z-50 bg-forest-900/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setSelected(null)}
                >
                    <div
                        className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-5 shadow-2xl relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setSelected(null)}
                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-forest-50 flex items-center justify-center"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <div>
                            <span className="text-[11px] font-bold uppercase tracking-widest text-gold-600">
                                Order
                            </span>
                            <h3 className="font-serif text-2xl font-bold text-forest-800">
                                #{selected.order_number}
                            </h3>
                        </div>
                        <div className="bg-cream rounded-2xl p-4 space-y-2 text-sm">
                            <Row k="Product" v={selected.product_name} />
                            <Row k="Bundle" v={selected.bundle_label} />
                            <Row k="Quantity" v={selected.bundle_quantity} />
                            {selected.subtotal ? (
                                <Row k="Subtotal" v={`${selected.subtotal.toLocaleString()} PKR`} />
                            ) : null}
                            {selected.delivery_fee ? (
                                <Row k="Delivery fee" v={`${selected.delivery_fee.toLocaleString()} PKR`} />
                            ) : null}
                            <Row k="Total" v={`${selected.total_price.toLocaleString()} PKR`} />
                            <Row k="Payment" v={(selected.payment_method || "cod").toUpperCase()} />
                            <hr className="border-forest-100" />
                            <Row k="Customer" v={selected.customer_name} />
                            <Row k="Phone" v={selected.phone} />
                            <Row k="City" v={selected.city} />
                            <Row k="Address" v={selected.address} />
                            {selected.email && <Row k="Email" v={selected.email} />}
                            {selected.notes && <Row k="Notes" v={selected.notes} />}
                            <Row k="Status" v={selected.status} />
                            <Row k="Placed" v={new Date(selected.created_at).toLocaleString()} />
                        </div>

                        {selected.payment_screenshot_url && (
                            <div
                                data-testid="payment-screenshot"
                                className="space-y-2"
                            >
                                <span className="text-[11px] font-bold uppercase tracking-widest text-gold-600">
                                    Payment screenshot
                                </span>
                                <a
                                    href={selected.payment_screenshot_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block border border-forest-100 rounded-2xl overflow-hidden bg-forest-50"
                                >
                                    <img
                                        src={selected.payment_screenshot_url}
                                        alt="Payment screenshot"
                                        className="w-full max-h-80 object-contain"
                                    />
                                </a>
                            </div>
                        )}
                        <a
                            href={statusWhatsAppLink(selected)}
                            data-testid="drawer-status-wa"
                            target="_blank"
                            rel="noreferrer"
                            className="block w-full bg-forest-700 hover:bg-forest-800 text-gold-400 text-xs font-bold py-3 rounded-xl text-center border border-gold-500/40"
                        >
                            Send "{selected.status}" update on WhatsApp
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between items-start gap-3">
            <span className="text-forest-700/60 text-xs uppercase tracking-widest font-bold">
                {k}
            </span>
            <span className="font-semibold text-forest-900 text-right">{v}</span>
        </div>
    );
}
